import { useEffect } from 'react';
import {
  initFetchByPerson,
  filterCompaniesByCreditCompanyId,
} from '@features/workspace/store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectIsAuthenticated, setCurrentUser } from '@store/features/auth';
import {
  setIsInitializing,
  setIsNoPersonResolved,
  setIsZafClientFailed,
} from '@store/features/loading';
import { isIframe } from '@utils/iframe';
import { zafClient } from '@app/services/zendesk-sdk';
import logger from '@app/services/logger';
import { fetchEmployee } from '@features/workspace/store/actions/fetch';

// Next 2 for zendesk support app
const SUPPORT_APP_PERSON_PATHS = [
  'ticket.requester.customField:contact_id',
  'ticket.requester.customField:person_id',
];

// Next 2 for zendesk sales app, have to be adjusted for multicompany too
const zendeskContactCompanyIdKey = 'contact.customFields:Company_ID';
const zendeskContactContactIdKey = 'contact.customFields:Contact_ID';

const zendeskCurrentUserKey = 'currentUser';
const zendeskTicketRequesterKey = 'ticket.requester';

export default function useResolvePersonId(
  personIdFromUri: string | null = null,
  needFetch = true
) {
  const dispatch = useAppDispatch();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  useEffect(() => {
    if (isAuthenticated && needFetch) {
      // eslint-disable-next-line
      const res = new Promise((resolve) => setTimeout(resolve, 1000)).then(
        resolveContactId
      );
    }
    // eslint-disable-next-line
  }, [isAuthenticated]);

  const resolveContactId = async () => {
    logger.debug(
      'UseResolveContact ZAFClient: going to resolve person id by SDK'
    );

    let tmpPersonId: string | null = null;

    const isZafClientAvailable = !!zafClient;

    // Checking that zafClient initialized when app embed
    if (!isZafClientAvailable) {
      logger.warn(
        'UseResolveContact ZAFClient: failed to resolve person ID...'
      );
      // If it happens in iframe its block process, showing warning to reload the page
      if (isIframe) {
        dispatch(setIsZafClientFailed(true));
        dispatch(setIsInitializing(false));

        return;
      }
    }

    // If its embed app use zafClient to resolve required info
    if (isZafClientAvailable) {
      await Promise.all(
        SUPPORT_APP_PERSON_PATHS.map(async (fieldPath) => {
          if (tmpPersonId) {
            return;
          }
          try {
            const zendeskContactUserIdRes = await zafClient.get(fieldPath);

            logger.debug(
              `UseResolveContact ZAFClient: fetched custom field by path "${fieldPath}"`,
              zendeskContactUserIdRes
            );
            if (zendeskContactUserIdRes?.[fieldPath]) {
              logger.debug(
                `UseResolveContact ZAFClient: person ID successfully resolved as "${zendeskContactUserIdRes[fieldPath]}"`
              );
              tmpPersonId = zendeskContactUserIdRes[fieldPath];
            }
          } catch (ex) {
            logger.warn(
              `UseResolveContact ZAFClient: path "${fieldPath}" not found`
            );
          }
        })
      );

      // For Sales app
      // Try to resolve as contact.customFields:Contact_ID
      if (!tmpPersonId) {
        try {
          const zendeskContactUserIdRes = await zafClient.get(
            zendeskContactContactIdKey
          );

          logger.debug(
            `UseResolveContact ZAFClient: fetched custom field by path "${zendeskContactContactIdKey}"`,
            zendeskContactUserIdRes
          );
          if (zendeskContactUserIdRes?.[zendeskContactContactIdKey]) {
            logger.debug(
              `UseResolveContact ZAFClient: person ID successfully resolved as "${zendeskContactUserIdRes[zendeskContactContactIdKey]}"`
            );
            tmpPersonId = zendeskContactUserIdRes[zendeskContactContactIdKey];
          }
        } catch (ex) {
          logger.warn(
            `UseResolveContact ZAFClient: path "${zendeskContactContactIdKey}" not found`
          );
        }
      }

      // Try to resolve as contact.customFields:Company_ID
      if (!tmpPersonId) {
        try {
          const zendeskCompanyIdRes = await zafClient.get(
            zendeskContactCompanyIdKey
          );

          logger.debug(
            `UseResolveContact ZAFClient: fetched custom field by path "${zendeskContactCompanyIdKey}"`,
            zendeskCompanyIdRes
          );
          if (zendeskCompanyIdRes?.[zendeskContactCompanyIdKey]) {
            const resolvedCompanyId =
              zendeskCompanyIdRes[zendeskContactCompanyIdKey];
            const companies = (
              await dispatch(
                filterCompaniesByCreditCompanyId(resolvedCompanyId)
              )
            )?.payload;

            logger.debug(
              `UseResolveContact ZAFClient: secondary fetched companies by resolved company id "${resolvedCompanyId}"`,
              companies
            );
            if (companies?.length) {
              const companyId = companies[0].id;
              const userId = companies[0].primaryUserId;
              const primaryEmployee = (
                await dispatch(
                  fetchEmployee({
                    companyId,
                    userId,
                  })
                )
              )?.payload;

              logger.debug(
                `UseResolveContact ZAFClient: fetched primary employee for company id "${companyId}" user id "${userId}"`,
                primaryEmployee
              );

              if (primaryEmployee) {
                logger.debug(
                  `UseResolveContact ZAFClient: person id successfully resolved as "${primaryEmployee.person.personId}"`
                );
                tmpPersonId = primaryEmployee.person.personId;
              }
            }
          }
        } catch (ex) {
          logger.warn(
            `UseResolveContact ZAFClient: path "${zendeskContactCompanyIdKey}" not found`
          );
        }
      }

      // Fetch ticket requester
      try {
        const ticketRequesterRes = await zafClient.get(
          zendeskTicketRequesterKey
        );

        logger.debug(
          'UseResolveContact ZAFClient: ticket requester',
          ticketRequesterRes
        );
      } catch (err) {
        logger.warn(
          `UseResolveContact ZAFClient: path "${zendeskTicketRequesterKey}" not found`
        );
      }

      // Fetch current zendesk user info
      try {
        const currentUserRes = await zafClient.get(zendeskCurrentUserKey);

        logger.debug(
          `UseResolveContact ZAFClient: fetched current user by path "${zendeskCurrentUserKey}"`,
          currentUserRes
        );
        if (currentUserRes?.[zendeskCurrentUserKey]) {
          logger.debug(
            'UseResolveContact ZAFClient: current zendesk user successfully resolved as',
            currentUserRes?.[zendeskCurrentUserKey]
          );
          dispatch(setCurrentUser(currentUserRes?.[zendeskCurrentUserKey]));
        }
      } catch (error) {
        logger.warn(
          `UseResolveContact ZAFClient: path "${zendeskCurrentUserKey}" not found`
        );
      }
    }

    // If zendesk sdk could not resolve person it - try to resolve by router path
    if (!tmpPersonId && personIdFromUri) {
      logger.debug(
        `UseResolveContact: person ID successfully resolved by uri param as "${personIdFromUri}" and stored to session`
      );
      tmpPersonId = personIdFromUri;
    }

    // If applet app opened in standalone window it doesn't have ZafClient, this way it tries to check if person id stored in session
    if (
      !tmpPersonId &&
      !isZafClientAvailable &&
      sessionStorage.getItem('active-person-id')
    ) {
      logger.debug(
        `UseResolveContact: person id successfully resolved by sessionStore as "${sessionStorage.getItem(
          'active-person-id'
        )}"`
      );
      tmpPersonId = sessionStorage.getItem('active-person-id');
    }

    // Save resolved user id to session and redux
    if (tmpPersonId && tmpPersonId !== 'undefined') {
      logger.debug(
        `UseResolveContact: Success. Setting active person id to session "${tmpPersonId}"`
      );
      sessionStorage.setItem('active-person-id', tmpPersonId);
      await dispatch(initFetchByPerson(tmpPersonId));
    } else {
      // eslint-disable-next-line quotes
      logger.warn("UseResolveContact: Failed. Setting 'No person resolved'");
      dispatch(setIsInitializing(false));
      dispatch(setIsNoPersonResolved(true));
    }
  };

  return null;
}
